/**
 * The single place officer-action analytics event names are defined.
 *
 * Keyed by the officer action a name belongs to. The officer portal maps a
 * failed action to the base name here; failures append `_ERROR` (only the
 * failure variants are tracked today, but the resolver supports both outcomes
 * so success events can be added later without new names elsewhere).
 *
 * Naming convention: `<ACTION>_<SUBJECT>` in CAPITAL_SNAKE — matching the
 * fetch-event-names convention. Names are code-level defaults; BI filters by
 * event name + the standard properties (service_name, institution_name, etc.).
 *
 * Note: the escalate-modal file upload is intentionally its own event
 * (`OFFICER_ATTACHMENT_UPLOAD`) and does NOT reuse the formly
 * `CUSTOMFILEUPLOAD_FILE_UPLOAD` name — that one belongs to the applicant
 * application-form upload widget, a different component and context.
 */
export const OFFICER_ACTION_EVENT_NAMES = {
  review: 'REVIEW_APPLICATION',
  approve: 'APPROVE_APPLICATION',
  reject: 'REJECT_APPLICATION',
  escalate: 'ESCALATE_APPLICATION',
  requestForAction: 'REQUEST_FOR_ACTION',
  upload: 'OFFICER_ATTACHMENT_UPLOAD',
  // UI-interaction guards: these fire only if opening the control itself throws
  // (not an API failure). `escalate` above is the escalate SUBMIT; this is the
  // escalate BUTTON opening the modal.
  escalateButton: 'ESCALATE_BUTTON',
  moreActions: 'MORE_ACTIONS_BUTTON',
} as const;

/** The officer actions that have an analytics event name. */
export type OfficerAnalyticsAction = keyof typeof OFFICER_ACTION_EVENT_NAMES;

/** Fallback when an action has no entry above. */
export const OFFICER_ACTION_EVENT_NAME_FALLBACK = 'OFFICER_ACTION';

/** Resolve the event name for an (action, outcome) pair. */
export function resolveOfficerActionEventName(
  action: OfficerAnalyticsAction,
  outcome: 'success' | 'failure'
): string {
  const base =
    OFFICER_ACTION_EVENT_NAMES[action] ?? OFFICER_ACTION_EVENT_NAME_FALLBACK;
  return outcome === 'failure' ? `${base}_ERROR` : base;
}
